// vocab.ts
// The ONE place where webhook enum values (exactly as the API returns them)
// become UI labels, pill colours and icons. Every surface — delivery rows,
// detail pages, endpoint cards, the alert banner, the summary card — reads
// from here so a status never has two names or two colours.
//
// Tailwind note: backgrounds use the /10 opacity step on purpose. The
// ring-pill idiom this is modelled on (CoinbaseOrdersTracker) uses /12,
// which Tailwind 3.4 does not generate for new files.

import {
  AlertTriangle,
  CheckCircle2,
  Circle,
  Clock,
  Loader2,
  MinusCircle,
  PauseCircle,
  RefreshCw,
  XCircle,
} from "lucide-react";
import type {
  AttemptOutcome,
  DeliveryBucket,
  DeliveryStatus,
  DeliveryTrigger,
  EndpointHealth,
  SkipReason,
  WebhookAlert,
} from "../../../services/merchantApi";
import type { PillUi } from "../ui";
import { formatCount } from "./format";

const PILL_GREEN = "bg-[#61CE70]/10 text-[#61CE70] ring-[#61CE70]/30";
const PILL_AMBER = "bg-amber-500/10 text-amber-300 ring-amber-500/30";
const PILL_BLUE = "bg-blue-500/10 text-blue-400 ring-blue-500/30";
const PILL_RED = "bg-red-500/10 text-red-400 ring-red-500/30";
const PILL_GRAY = "bg-gray-500/10 text-gray-300 ring-gray-500/30";

// ── Delivery status ─────────────────────────────────────────────────────
// `label` is the short form (fits a list row). Use deliveryStatusLabel()
// for the long form that carries attempt counts / the skip reason.
// `bucket` is the matching value of the history filter (?status=).
export const DELIVERY_STATUS_UI: Record<DeliveryStatus, PillUi & { bucket: DeliveryBucket }> = {
  pending: { label: "Pending", cls: PILL_AMBER, icon: Clock, bucket: "in_progress" },
  delivering: { label: "Sending", cls: PILL_BLUE, icon: Loader2, spin: true, bucket: "in_progress" },
  retrying: { label: "Retrying", cls: PILL_AMBER, icon: RefreshCw, bucket: "in_progress" },
  succeeded: { label: "Delivered", cls: PILL_GREEN, icon: CheckCircle2, bucket: "succeeded" },
  failed: { label: "Failed", cls: PILL_RED, icon: XCircle, bucket: "failed" },
  skipped: { label: "Skipped", cls: PILL_GRAY, icon: MinusCircle, bucket: "skipped" },
};

export const SKIP_REASON_LABEL: Record<SkipReason, string> = {
  endpoint_paused: "endpoint paused",
  endpoint_disabled: "endpoint disabled by CashTap",
  endpoint_deleted: "endpoint deleted",
  superseded: "session completed later",
  backlog_limit: "too many deliveries waiting",
};

// Long status label: "Retrying · attempt 3 of 6", "Failed · all retries
// used", "Skipped · endpoint paused". Single-attempt deliveries (test /
// resend) have no retries to use up, so they read plain "Failed".
export function deliveryStatusLabel(d: {
  status: DeliveryStatus;
  skipReason?: SkipReason | null;
  attemptCount?: number;
  maxAttempts?: number;
}): string {
  const base = DELIVERY_STATUS_UI[d.status]?.label ?? String(d.status);
  if (d.status === "retrying" && d.maxAttempts) {
    return `${base} · attempt ${Math.max(1, d.attemptCount || 0)} of ${d.maxAttempts}`;
  }
  if (d.status === "failed" && (d.maxAttempts || 0) > 1) return `${base} · all retries used`;
  if (d.status === "skipped" && d.skipReason) {
    return `${base} · ${SKIP_REASON_LABEL[d.skipReason] ?? d.skipReason}`;
  }
  return base;
}

// ── Attempt outcome (one row of the attempt timeline) ───────────────────
// A failed attempt is never the bare word "Failed" — that is reserved for
// a delivery that has used all its retries. `dot` colours the timeline dot.
export const ATTEMPT_OUTCOME_UI: Record<
  AttemptOutcome,
  { label: string; text: string; dot: string; detail: string | null }
> = {
  succeeded: { label: "Delivered", text: "text-[#61CE70]", dot: "bg-[#61CE70]", detail: null },
  failed: { label: "Attempt failed", text: "text-red-400", dot: "bg-red-400", detail: null },
  interrupted: {
    label: "Interrupted",
    text: "text-gray-300",
    dot: "bg-gray-500",
    detail:
      "Interrupted — CashTap restarted while sending. Retried automatically; your server may have received this attempt.",
  },
};

// ── Endpoint health ─────────────────────────────────────────────────────
export const HEALTH_UI: Record<EndpointHealth, PillUi> = {
  healthy: { label: "Healthy", cls: PILL_GREEN, icon: CheckCircle2 },
  failing: { label: "Failing", cls: PILL_AMBER, icon: AlertTriangle },
  disabled: { label: "Disabled by CashTap", cls: PILL_RED, icon: XCircle },
  paused: { label: "Paused by you", cls: PILL_GRAY, icon: PauseCircle },
  idle: { label: "No deliveries yet", cls: PILL_GRAY, icon: Circle },
};

// Health pill for an endpoint. "Failing" turns from amber to red once a
// delivery has terminally failed and has not been reviewed yet.
export function healthUi(e: { health: EndpointHealth; failedSinceAck?: number }): PillUi {
  const ui = HEALTH_UI[e.health] ?? HEALTH_UI.idle;
  if (e.health === "failing" && (e.failedSinceAck || 0) > 0) return { ...ui, cls: PILL_RED };
  return ui;
}

// ── Summary alerts (banner + API Settings card) ─────────────────────────
/** Display copy + link target for one summary alert. */
export interface AlertCopy {
  title: string;
  body: string;
  ctaLabel: string;
  ctaTo: string;
  /** `deliveries_failed` only: can be acknowledged ("Mark as reviewed"). */
  ackable: boolean;
}

// One source for the alert wording, so the Home banner, the page banner
// and the API Settings card can never disagree. `urlHost` and the error
// message are untrusted strings — callers render them as text only.
export function describeAlert(a: WebhookAlert): AlertCopy {
  const endpointTo = `/developers/webhooks/endpoints/${encodeURIComponent(a.endpointId)}`;
  if (a.kind === "endpoint_disabled") {
    return {
      title: `Webhook endpoint ${a.urlHost} was disabled`,
      body: "It failed for 3 days in a row. New events are being skipped. You can resend skipped events from the delivery history.",
      ctaLabel: "Open endpoint",
      ctaTo: endpointTo,
      ackable: false,
    };
  }
  if (a.kind === "deliveries_failed") {
    const n = a.failedDeliveries;
    return {
      title:
        n === 1
          ? `1 webhook delivery to ${a.urlHost} failed permanently`
          : `${formatCount(n)} webhook deliveries to ${a.urlHost} failed permanently`,
      body: "All retries were used. Review them and resend once your endpoint is fixed.",
      ctaLabel: "Review failed deliveries",
      ctaTo: `/developers/webhooks?tab=deliveries&endpoint=${encodeURIComponent(a.endpointId)}&status=failed`,
      ackable: true,
    };
  }
  // endpoint_failing — we are still retrying on our own.
  const deliveryId = a.lastError?.deliveryId || null;
  return {
    title: `Webhook deliveries to ${a.urlHost} are failing`,
    body: `${a.lastError?.message ? `${a.lastError.message} ` : ""}We're retrying automatically.`,
    ctaLabel: deliveryId ? "View delivery" : "Open endpoint",
    ctaTo: deliveryId
      ? `/developers/webhooks/deliveries/${encodeURIComponent(deliveryId)}`
      : endpointTo,
    ackable: false,
  };
}

// ── Trigger tag ─────────────────────────────────────────────────────────
// `event` (a real delivery) carries no tag, so it has no entry:
// `const tag = TRIGGER_TAG[d.trigger];`
// `{tag && <Tag tone={tag.tone}>{tag.label}</Tag>}`
export const TRIGGER_TAG: Partial<Record<DeliveryTrigger, { label: string; tone: "blue" }>> = {
  test: { label: "TEST", tone: "blue" },
  resend: { label: "RESEND", tone: "blue" },
};

// ── Event types ─────────────────────────────────────────────────────────
// Friendly names for selects and summaries. Rows and detail pages print
// the raw type — that is the string developers grep their code for.
export const EVENT_TYPE_LABEL: Record<string, string> = {
  "checkout.session.completed": "Payment completed",
  "checkout.session.expired": "Session expired",
  "checkout.session.failed": "Settlement failed",
};

// ── Attempt error codes → chip text (shown when there is no HTTP status) ─
export const ERROR_CODE_LABEL: Record<string, string> = {
  http_error: "HTTP",
  redirect_not_followed: "REDIRECT",
  timeout: "TIMEOUT",
  tls_error: "TLS",
  connection_refused: "REFUSED",
  connection_reset: "RESET",
  network_unreachable: "NETWORK",
  network_error: "NETWORK",
  dns_not_found: "DNS",
  dns_timeout: "DNS",
  dns_error: "DNS",
  dns_busy: "DNS",
  blocked_address: "BLOCKED",
  invalid_url: "BAD URL",
  payload_too_large: "TOO LARGE",
  secret_unavailable: "ERR",
  worker_interrupted: "INTERRUPTED",
  internal_error: "ERR",
};

// Colour of the HTTP status chip. No status (network-level failure) and
// anything outside 2xx-4xx is red.
export function httpChipClass(status: number | null | undefined): string {
  if (typeof status === "number") {
    if (status >= 200 && status < 300) return "bg-[#61CE70]/15 text-[#61CE70]";
    if (status >= 300 && status < 400) return "bg-blue-500/15 text-blue-300";
    if (status >= 400 && status < 500) return "bg-amber-500/15 text-amber-300";
  }
  return "bg-red-500/15 text-red-300";
}

const PLATFORM_FAULT_HINT =
  "This was a problem on CashTap's side, not your server's. It is retried automatically.";
const UNREACHABLE_HINT =
  "We could not reach your server. Check that it is running, listening on 443/8443 and that a firewall is not blocking CashTap.";
const DNS_HINT = "The hostname did not resolve. Check the DNS records for this domain.";

// "How to fix" text for an error. The API already sends `hint` with every
// error — prefer it: `err.hint ?? fallbackHint(err.code, err.responseStatus)`.
// This mirrors the backend's table for the rare row that arrives without
// one. Returns null when there is nothing useful to say.
export function fallbackHint(
  code: string | null | undefined,
  status: number | null | undefined,
): string | null {
  switch (code) {
    case "http_error": {
      const s = typeof status === "number" ? status : 0;
      if (s === 400 || s === 422) {
        return "Your server rejected the payload. Make sure the route reads the raw body and does not validate it against a different schema.";
      }
      if (s === 401 || s === 403) {
        return "Your server refused the request. Webhooks carry no Authorization header: exempt this route from auth/CSRF/firewall rules and verify the X-CashTap-Signature instead.";
      }
      if (s === 404 || s === 405) {
        return "No handler answered at this URL for POST. Check the path and that the route is deployed.";
      }
      if (s === 408 || s === 429) {
        return "Your server is throttling or timing out. Return 2xx first and process in the background.";
      }
      if (s >= 500 && s < 600) {
        return "Your server returned an error. Check its logs for this delivery id, fix the handler, then use Retry now.";
      }
      return "Your server must answer with any 2xx status to acknowledge the event.";
    }
    case "redirect_not_followed":
      return "CashTap does not follow redirects. Set the endpoint URL to the final https address (watch for http→https or trailing-slash redirects).";
    case "timeout":
      return "No response within 10 seconds. Acknowledge immediately with 2xx and do slow work asynchronously.";
    case "tls_error":
      return "The TLS certificate was rejected (expired, self-signed, wrong host or missing intermediate). Test with an SSL checker and install the full chain.";
    case "connection_refused":
    case "connection_reset":
    case "network_unreachable":
    case "network_error":
      return UNREACHABLE_HINT;
    case "dns_not_found":
    case "dns_timeout":
    case "dns_error":
      return DNS_HINT;
    case "blocked_address":
      return "The hostname resolves to a private or reserved IP address. Webhooks must target a public address; use a tunnel for local development.";
    case "invalid_url":
      return "The saved URL no longer passes validation. Edit the endpoint and save a valid https URL.";
    case "payload_too_large":
      return "The event payload exceeds the maximum webhook size. Contact support.";
    case "worker_interrupted":
    case "dns_busy":
    case "secret_unavailable":
    case "internal_error":
      return PLATFORM_FAULT_HINT;
    default:
      return null;
  }
}
